import {
  CreateReceiptRequest, Product, Receipt,
  ReceiptQuota, ScanReceiptRequest, ScanReceiptResponse, UploadReceiptRequest,
} from './receiptModel';
import axios, {AxiosError, AxiosRequestConfig} from 'axios';
import {get, post, put, instance, responseParser, errorParser} from "../core";
import {ErrorResponse} from "../models/error-response";


export const receiptApi = () => {

  const URL_RECEIPT = '/v1/receipt'

  const publicInstance = axios.create({
    baseURL: instance.defaults.baseURL,
    withCredentials: false,
    xsrfCookieName: '',
    // XHR's withCredentials=false still sends same-origin cookies.
    adapter: async config => {
      const url = `${(config.baseURL || '').replace(/\/+$/, '')}/${config.url!.replace(/^\/+/, '')}`;
      let response: Response;
      try {
        response = await fetch(url, {
          method: 'GET',
          credentials: 'omit',
          headers: {Accept: config.responseType === 'blob' ? 'image/*' : 'application/json'},
        });
      } catch {
        throw new Error('Network Error');
      }
      return {
        data: config.responseType === 'blob' ? await response.blob() : await response.text(),
        status: response.status,
        statusText: response.statusText,
        headers: {},
        config,
      };
    },
  });
  publicInstance.interceptors.request.use(config => ({
    ...config,
    // Do not inherit stale bearer/basic credentials from global Axios defaults.
    headers: {'Content-Type': 'application/json'},
    auth: undefined,
    withCredentials: false,
  }));

  const publicGet = async <T>(url: string, responseType: AxiosRequestConfig['responseType'] = 'json'): Promise<T> => {
    try {
      const response = await publicInstance.get(url, {
        baseURL: instance.defaults.baseURL,
        responseType,
      });
      return responseParser(response);
    } catch (error) {
      return errorParser(error as AxiosError);
    }
  };

  const create = (
    request: CreateReceiptRequest
  ): Promise<Receipt & ErrorResponse> => {
    return post(`${URL_RECEIPT}/create`, request);
  };

  const getReceiptDetails = (id?: string | string[]): Promise<Receipt & ErrorResponse> => {

    try {
      const idNumber = Number(id);
      return get(`${URL_RECEIPT}/details`, {id: idNumber});
    } catch (e) {
      throw new Error("Something went wrong " + e)
    }
  };


  const uploadReceipt = (request: UploadReceiptRequest): Promise<Product[] & ErrorResponse> => {
    try {
      return post(`${URL_RECEIPT}/upload`, request);
    } catch (e) {
      throw new Error("Something went wrong " + e)
    }
  };

  const getQuota = (): Promise<ReceiptQuota & ErrorResponse> => {
    return get(`${URL_RECEIPT}/quota`);
  };

  const scan = (request: ScanReceiptRequest): Promise<ScanReceiptResponse & ErrorResponse> => {
    return post(`${URL_RECEIPT}/scan`, request);
  };

  const update = (id: number, request: CreateReceiptRequest & {version: number}): Promise<Receipt> => {
    return put(`${URL_RECEIPT}/${id}`, request);
  };

  const createShareLink = (id: number): Promise<{token: string}> => {
    return post(`${URL_RECEIPT}/${id}/share`);
  };

  const getSharedReceipt = (token: string): Promise<Receipt> => {
    return publicGet(`/public/receipts/${encodeURIComponent(token)}`);
  };

  const getImage = (id: number): Promise<Blob> => {
    return get(`${URL_RECEIPT}/${id}/image`, undefined, {responseType: 'blob'});
  };

  const getSharedImage = (token: string): Promise<Blob> => {
    return publicGet(`/public/receipts/${encodeURIComponent(token)}/image`, 'blob');
  };

  return {
    create,
    getReceiptDetails,
    uploadReceipt,
    getQuota,
    scan,
    update,
    createShareLink,
    getSharedReceipt,
    getImage,
    getSharedImage,
  };
};
